import {
  ICreateApplicationFormAnswerPool,
  ICreateApplicationFormAnswerValue,
} from '@boldpenguin/sdk';
import { IAcordFormPdf } from '../services/acord-forms.service';
import { EmperorUISelectFormChoice } from '@boldpenguin/emperor-form-fields';

// Enums
export enum FileTypes {
  JSON = 'application/json',
  SAGITTA = 'Sagitta',
}

export enum AlertTooltip {
  RemoveFile = 'Remove File',
  Dismiss = 'Dismiss',
}

export enum UploadFormQuestionCodes {
  FIRST_NAME = 'mqs_first_name',
  LAST_NAME = 'mqs_last_name',
  BUSINESS_NAME = 'mqs_business_name',
  YEAR_ESTABLISHED = 'mqs_year_established',
  PHONE = 'mqs_phone',
  EMAIL = 'mqs_email',
  STREET = 'mqs_street_1',
  STREET2 = 'mqs_street_2',
  CITY = 'mqs_city',
  STATE = 'mqs_state',
  ZIPCODE = 'mqs_zipcode',
  COVERAGE_TYPES = 'CoverageTypes',
  FEIN = 'mqs_fein',
  LEGAL_ENTITY = 'mqs_legal_entity',
  HAS_FEIN = 'mqs_has_fein',
  NAICS = 'Naics',
  COVERAGE_START_DATE = 'mqs_coverage_start_date',
  FULL_TIME_EMPLOYEES = 'mqs_full_time_employees',
  PART_TIME_EMPLOYEES = 'mqs_part_time_employees',
  EMPLOYEES = 'mqs_employees',
  CURRENTLY_INSURE = 'mqs_currently_insured',
  COVERAGE_LIABILITY_LIMIT = 'mqs_covg_ca_liability_limit',
  BUILDING_CONSTRUCTION = 'mqs_building_construction',
  BUILDING_STORIES = 'mqs_building_stories',
  BUILDING_SQUARE_FOOTAGE = 'mqs_building_square_footage',
  BUILDING_YEAR_BUILD = 'mqs_building_year_built',
  BUSINESS_PERSONAL_PROPERTY_LIMIT = 'mqs_business_personal_property_limit',
  BUILDING_LIMIT = 'mqs_building_limit',
  BUILDING_HAS_SPRINKLER_SYSTEM = 'mqs_building_has_sprinkler_system',
}

export enum YesOrNo {
  YES = 'Yes',
  NO = 'No',
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
}

export enum QuestionPoolCode {
  Location = 'locations',
  Jobs = 'jobs',
  Building = 'buildings',
  Owners = 'owners',
  AdditionalInsureds = 'additional_insureds',
  Vehicles = 'vehicles',
  Drivers = 'drivers',
}

export enum EmployeeFieldTypes {
  NoEmployeesFullTime = 'NoEmployeesFullTime',
  NoEmployeesPartTime = 'NoEmployeesPartTime',
  ClassNoEmpl = 'ClassNoEmpl',
}

// Aliases - for making some types comprehensible

export type LocationNo = string;
export type BuildingNo = string;
export type AnswerMap = Record<string, string | string[] | undefined>;

// Interfaces
export interface AcordUiMessage {
  title: string;
  success: boolean;
  subtitle?: string;
  acordPdf?: IAcordFormPdf;
  closeAction?: () => unknown;
}

export interface DocumentUpload {
  filename: string;
  content_type: string;
  data: Record<string, string | string[]>;
}

export interface AppData {
  answer_values: ICreateApplicationFormAnswerValue[];
  answer_pools?: ICreateApplicationFormAnswerPool[];
}

export interface AddressMap {
  [key: string]: {
    short: string;
    long: string;
  };
}

// Hardcode const moved into the model
export const AnsweredByType = 'DocumentUpload';
export const AnsweredBySource = 'Document upload';
export const CoverageLocation = 'NE.POLICIES.EXTRATAGS';
export const AdditionalInterestsLocation = 'ADDITIONAL.INTERESTS';
export const TrailerBodyType = 'trailer';

export enum VehicleFlowOptions {
  YMM = 'Year / Make / Model',
  VIN = 'VIN',
}

// Map Sagitta coverage codes to ours
export const SagittaCoverages = {
  AUTOB: 'AUTOB',
  CGL: 'CGL',
  POLL: 'POLL',
  PROP: 'BOP',
  WORK: 'WORK',
};

export const SagittaLegalEntities = {
  LP: 'Partnership',
  CP: 'C Corporation',
  AS: 'Association',
  CA: 'Association',
  CR: 'C Corporation',
  JV: 'Joint Venture',
  LL: 'LLC',
  MU: 'Municipality',
  IP: 'Partnership',
  PC: 'Partnership',
  PT: 'Partnership',
  SR: 'S Corporation',
  TR: 'Trust',
  OTHER: 'Other',
};

export const SagittaConstructionType: Record<string, string | undefined> = {
  'Fire Resistive/Superior': 'Fire Resistive',
  'Joisted Masonry': 'Joisted Masonry',
  'Non-Combustible': 'Non Combustible',
};

export const documentUploadValidTypes = [
  'application/pdf',
  'image/*',
  'text/plain',
  'application/json',
].join(',');

export const stateChoices: EmperorUISelectFormChoice[] = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
].map((state) => ({
  value: state,
  label: state,
  id: state,
}));

export const VehicleUseMap: Record<string, string | undefined> = {
  ['Pleasure']: 'Personal use only',
  ['Service']: 'Business use only',
  ['Retail']: 'Business use only',
  ['Commercial']: 'Business use only',
  ['Passenger']: 'Business use only',
  ['Over 15 Miles Drive to Work or School']: 'Business use only',
  ['Under 15 Miles Drive to Work or School']: 'Business use only',
  ['Farm']: 'Business use only',
  ['Day Care Center']: 'Business use only',
  ['For Hire']: 'Business use only',
  ['APPT reg. with state as non-comm, used to transport']: 'Business use only',
};

export const BodyTypeCodeMap: Record<string, string | undefined> = {
  ['2D']: 'Automobiles',
  ['2DRSN']: 'Automobiles',
  ['4DRSN']: 'Automobiles',
  ['5D']: 'Automobiles',
  ['BT']: 'Trailers',
  ['CT']: 'Trailers',
  ['DMPST']: 'Trailers',
  ['DMPT']: 'Trailers',
  ['MT']: 'Trailers',
  ['MV']: 'Minivans',
  ['REC']: 'Trailers',
  ['SEDAN']: 'Automobiles',
  ['SHRMT']: 'Trailers',
  ['SRVT']: 'Trailers',
  ['ST']: 'Trailers',
  ['STAWG']: 'Station Wagons',
  ['STR5']: 'Trailers',
  ['STWAG']: 'Station Wagons',
  ['T']: 'Trailers',
  ['TCDSD']: 'Trailers',
  ['UT']: 'Trailers',
};

export const ComprehensiveDeductibleMap: Record<string, string | undefined> = {
  '250': '$250 deductible',
  '500': '$500 deductible',
  '1000': '$1,000 deductible',
};

export const uploadFormLayout: UploadFormQuestion[][] = [
  [
    {
      code: UploadFormQuestionCodes.FIRST_NAME,
      text: "Policyholder's First Name",
      type: 'text',
    },
    {
      code: UploadFormQuestionCodes.LAST_NAME,
      text: "Policyholder's Last Name",
      type: 'text',
    },
  ],
  [
    { code: UploadFormQuestionCodes.PHONE, text: 'Phone Number', type: 'text' },
    {
      code: UploadFormQuestionCodes.EMAIL,
      text: "Policyholder's email address",
      type: 'text',
    },
  ],
  [
    {
      code: UploadFormQuestionCodes.BUSINESS_NAME,
      text: 'Business Name',
      type: 'text',
    },
  ],
  [
    {
      code: UploadFormQuestionCodes.STREET,
      text: 'Street address',
      type: 'address',
    },
    {
      code: UploadFormQuestionCodes.STREET2,
      text: 'Suite / Apt Number',
      type: 'text',
    },
  ],
  [
    { code: UploadFormQuestionCodes.CITY, text: 'City', type: 'text' },
    { code: UploadFormQuestionCodes.ZIPCODE, text: 'Zipcode', type: 'text' },
    {
      code: UploadFormQuestionCodes.STATE,
      text: 'State',
      type: 'select',
      choices: stateChoices,
    },
  ],
];

// Other
type UploadFormQuestion = {
  code: string;
  text: string;
  type: 'text' | 'select' | 'address';
  choices?: EmperorUISelectFormChoice[];
};

export enum IUploadDialogSource {
  MAIN_MENU = 'Main Menu',
  QUOTES_DASHBOARD_CARD = 'Quotes Dashboard Card',
  QUOTES_HEADER = 'Quotes Header',
}
